<p-sidebar
  [visible]="aiPanelService.visible()"
  (visibleChange)="aiPanelService.set($event)"
  position="right"
  [fullScreen]="isFullscreen()"
  [modal]="false"
  [dismissible]="false"
  [showCloseIcon]="false"
  [baseZIndex]="3000"
  styleClass="ai-panel">
  <!-- Header — soft pink-to-lavender gradient ("AI surface" treatment
       used across Mia chrome). Approximates the Figma Cross-Channel
       MASTER comp: pale pink at the title edge, white through the
       middle, pale lavender at the close-button edge. -->
  <ng-template pTemplate="header">
    <div
      class="flex items-center justify-between w-full px-4 py-3 border-b border-gray-300 bg-gradient-to-r from-[#fce7e5] via-white to-[#e8e2f2]">
      <span class="font-semibold text-base text-gray-1100">AI Assistant</span>
      <div class="flex items-center gap-1">
        <button
          type="button"
          class="flex items-center justify-center bg-transparent border-0 cursor-pointer rounded p-1.5 text-gray-600 hover:text-gray-900 hover:bg-white/60 transition-colors"
          [title]="isFullscreen() ? 'Minimize' : 'Expand'"
          (click)="toggleFullscreen()">
          <span class="material-symbols-outlined text-lg">{{
            isFullscreen() ? "close_fullscreen" : "open_in_full"
          }}</span>
        </button>
        <button
          type="button"
          class="flex items-center justify-center bg-transparent border-0 cursor-pointer rounded p-1.5 text-gray-600 hover:text-gray-900 hover:bg-white/60 transition-colors"
          title="Close"
          (click)="isFullscreen.set(false); aiPanelService.set(false)">
          <span class="material-symbols-outlined text-lg">close</span>
        </button>
      </div>
    </div>
    @if (hasInvokedAgent()) {
      <div class="flex items-center gap-2 px-4 py-3 border-b border-gray-300">
        <button
          type="button"
          class="flex items-center justify-center bg-transparent border-0 cursor-pointer rounded p-1.5 text-gray-900 hover:text-gray-1100 hover:bg-gray-200 transition-colors"
          title="Back to prompts"
          aria-label="Back to prompts"
          (click)="resetThread()">
          <span class="material-symbols-outlined text-lg">arrow_back</span>
        </button>
        <span class="font-semibold text-[0.9375rem] text-gray-1100">{{
          activeAgentName()
        }}</span>
      </div>
    }
  </ng-template>

  <!-- Content — overflow-x:hidden so agent-table scroll containers are
       bounded by the panel's own width rather than expanding it.

       Two-state model:
         prompts state — !hasInvokedAgent → welcome + starter tabs
         agent state   →  hasInvokedAgent → chat thread + live in-flight -->
  <div
    class="flex-1 overflow-y-auto overflow-x-hidden p-4 flex flex-col min-h-0">
    @if (!hasInvokedAgent()) {
      <portal-prompts-view
        [userDisplayName]="userDisplayName"
        [myStarters]="myStarters()"
        [sharedStarters]="sharedStarters()"
        [tabHeaders]="starterTabHeaders()"
        [loading]="panelConfigQuery.isLoading()"
        [responding]="isAgentResponding()"
        (tabChange)="onStarterTabChange($event)"
        (askStarter)="askConversationStarter($event)" />
    } @else {
      <portal-chat-thread
        [blocks]="chat.displayBlocks()"
        [responding]="isAgentResponding()"
        [liveSteps]="chat.liveSteps()"
        [streamingDraft]="chat.streamingDraft()" />
    }
  </div>

  <!-- Footer — hidden until an agent has been invoked so the welcome screen
       stays clean. -->
  <ng-template pTemplate="footer">
    @if (hasInvokedAgent()) {
      <div class="px-4 pt-3 pb-3 bg-gray-000 border-t border-gray-300">
        <div
          class="flex items-center gap-3 border border-gray-300 rounded-full px-4 py-2 mb-2 focus-within:border-blue-400">
          <span
            class="material-symbols-outlined flex-shrink-0 text-lg text-gray-500"
            >alternate_email</span
          >
          <div class="flex-1 relative">
            <input
              #chatInput
              type="text"
              class="w-full b2 !p-0 text-gray-900 !bg-transparent !border-none !rounded-none !outline-none !shadow-none placeholder:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              [placeholder]="
                isAgentResponding()
                  ? 'Waiting for response...'
                  : 'Ask a follow-up'
              "
              [ngModel]="chatInputValue()"
              (input)="onChatInput($event)"
              (keyup.enter)="sendMessage()"
              [disabled]="isAgentResponding()" />
            @if (
              mentionDropdownVisible() && filteredMentionAgents().length > 0
            ) {
              <div
                class="absolute bottom-full left-0 mb-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden z-10 min-w-[200px]">
                @for (
                  agent of filteredMentionAgents();
                  track agent.assistantId
                ) {
                  <button
                    type="button"
                    class="flex items-center gap-2 w-full px-3 py-2 text-left text-sm bg-transparent border-0 cursor-pointer text-gray-900 hover:bg-gray-200"
                    (click)="selectMentionAgent(agent)">
                    @if (agent.icon) {
                      <span
                        class="material-symbols-outlined text-base text-gray-500"
                        >{{ agent.icon }}</span
                      >
                    }
                    <span class="font-medium">{{ agent.agentName }}</span>
                    @if (agent.isOwnedByMe) {
                      <span
                        class="px-1 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide rounded-full text-orange-400 bg-orange-100"
                        >mine</span
                      >
                    }
                  </button>
                }
              </div>
            }
          </div>
          <button
            type="button"
            class="flex items-center justify-center border-0 cursor-pointer rounded-full flex-shrink-0 w-8 h-8 text-white bg-blue-600 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            [disabled]="isAgentResponding() || !chatInputValue()"
            (click)="sendMessage()">
            <span class="material-symbols-outlined text-base">send</span>
          </button>
        </div>
        <p class="text-center m-0 text-xs text-gray-500">
          AI can make mistakes. Please double-check responses.
        </p>
      </div>
    }
  </ng-template>
</p-sidebar>
